import IMediaOwnerTile from '@/common/components/controls/targeting/media-owners/MediaOwnerTile';
import {
  Account,
  AudienceLogic,
  Campaign,
  DeliveryStatistics,
  Demographic,
  Network,
  Organization,
  PreSaleHeatmapApiModel,
} from '@/common/models';
import Advertiser from '@/common/models/advertisers/Advertiser';
import PackageGroup from '@/common/models/package-groups/PackageGroup';
import Plan from '@/common/models/plan/Plan';
import BudgetBreakdownService from '@/org-app/components/plans/budgetBreakdown/BudgetBreakdownService';

import BudgetBreakdownData from './BudgetBreakdownData';
import { CreativeSizeData, ICreativeSizeData } from './CreativeSizeData';
import { ILocationTargetingData, LocationTargetingData } from './LocationTargetingData';

export type PlanExtra = Plan & {
  advertiser: Advertiser;
  account: Account;
};

export interface ISharedPlanData {
  plan: PlanExtra;
  mediaOwners?: {
    mediaOwnersForSelectedTiles: IMediaOwnerTile[];
  };
  audienceInfo?: DeliveryStatistics;
  budget?: {
    budget: string;
    estimatedImpressions: string;
    ecpm: string;
  };
  playback?: {
    networkFrequencyCaps: Array<{
      network: Network;
      minSecondsBetweenPlays: number;
    }>;
  };
  deals?: {
    packageGroup: PackageGroup;
  };
  demographics?: {
    demographics: Demographic[];
    outOfDateCustomAudiences: AudienceLogic[];
  };
  locationTargeting?: ILocationTargetingData;
  inventory?: {
    geocodedLocationsGeoJson: {
      type: string;
      features: Array<{
        type: string;
        geometry: {
          type: string;
          coordinates: [number, number];
        };
        id: number;
        properties: Record<string, unknown>;
      }>;
    };
    estimatedDeliveryStats: {
      audienceImpressions: {
        isVisible: boolean;
        value: string;
      };
      totalImpressions: string | number;
      selectedTypeCount: {
        value: string | number;
        label: string;
      };
      eCPM: string;
    };
  };
  heatmap?: PreSaleHeatmapApiModel;
  daypart?: string;
  creativeSize?: ICreativeSizeData;
  state: {
    orgApp: {
      organization: Organization;
      organizationSettings: Record<string, unknown>;
      ReferenceData;
    };
  };
  timestamp: string; // A Date in ISO format
  feedback?: boolean;
  budgetBreakdown?: BudgetBreakdownService[];

  // BE returns this value outside the json
  campaign?: Campaign;
}

export class SharedPlanData {
  public static deserialize(sharedPlanDataStr: string): ISharedPlanData {
    const parsedObject = JSON.parse(sharedPlanDataStr);
    const { plan, locationTargeting, heatmap, creativeSize, budgetBreakdown, ...other } = parsedObject;
    const planInternal = Plan.fromApi(plan);

    return {
      plan: planInternal,
      locationTargeting: LocationTargetingData.deserialize(locationTargeting),
      heatmap: heatmap ? PreSaleHeatmapApiModel.fromApi(heatmap) : null,
      creativeSize: CreativeSizeData.deserialize(creativeSize),
      budgetBreakdown: BudgetBreakdownData.deserialize(budgetBreakdown, planInternal),
      ...other,
    };
  }

  public static serialize(sharedPlanData: ISharedPlanData): string {
    const { plan, locationTargeting, heatmap, creativeSize, budgetBreakdown, ...other } = sharedPlanData;

    return JSON.stringify({
      plan: {
        ...plan.toApi(),
        account: plan.account?.toApi(),
        advertiser: plan.advertiser?.toApiWithUniqueAdvertiser(),
      },
      locationTargeting: LocationTargetingData.serialize(locationTargeting),
      heatmap: heatmap?.toApi(),
      creativeSize: CreativeSizeData.serialize(creativeSize),
      budgetBreakdown: BudgetBreakdownData.serialize(budgetBreakdown),
      ...other,
    });
  }
}
